export async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker available
              console.log('New service worker available. Refresh to update.');
              
              // Optionally show update prompt to user
              if (window.confirm('A new version is available. Refresh to update?')) {
                window.location.reload();
              }
            }
          });
        }
      });
      
      console.log('Service Worker registered successfully');
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
}

export async function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
  }
}

export async function requestNotificationPermission() {
  if ('Notification' in window && 'serviceWorker' in navigator) {
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      
      // Subscribe to push notifications
      const registration = await navigator.serviceWorker.ready;
      
      try {
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
          )
        });
        
        // Send subscription to server
        const { getApiBaseUrl } = await import('../config/api');
        await fetch(`${getApiBaseUrl()}/api/notifications/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(subscription)
        });
        
        return subscription;
      } catch (error) {
        console.error('Failed to subscribe to push notifications:', error);
      }
    }
    
    return null;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

// Background sync for offline messages
export async function scheduleMessageSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    const registration = await navigator.serviceWorker.ready;
    
    try {
      await (registration as any).sync.register('sync-messages');
      console.log('Background sync scheduled');
    } catch (error) {
      console.error('Background sync registration failed:', error);
    }
  }
}

// IndexedDB helpers for offline storage
export class OfflineStorage {
  private db: IDBDatabase | null = null;
  private dbName = 'companion-db';
  private version = 1;

  async init() {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        if (!db.objectStoreNames.contains('offline-messages')) {
          db.createObjectStore('offline-messages', { keyPath: 'id', autoIncrement: true });
        }
        
        if (!db.objectStoreNames.contains('cached-conversations')) {
          const conversationStore = db.createObjectStore('cached-conversations', { keyPath: 'id' });
          conversationStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async saveOfflineMessage(message: {
    content: string;
    messageId: string;
    token: string;
    timestamp: number;
  }) {
    if (!this.db) await this.init();
    
    const tx = this.db!.transaction(['offline-messages'], 'readwrite');
    const store = tx.objectStore('offline-messages');
    
    return store.add(message);
  }

  async getOfflineMessages() {
    if (!this.db) await this.init();
    
    const tx = this.db!.transaction(['offline-messages'], 'readonly');
    const store = tx.objectStore('offline-messages');
    
    return store.getAll();
  }

  async deleteOfflineMessage(id: number) {
    if (!this.db) await this.init();
    
    const tx = this.db!.transaction(['offline-messages'], 'readwrite');
    const store = tx.objectStore('offline-messages');
    
    return store.delete(id);
  }

  async cacheConversation(conversation: {
    id: string;
    messages: any[];
    timestamp: number;
  }) {
    if (!this.db) await this.init();
    
    const tx = this.db!.transaction(['cached-conversations'], 'readwrite');
    const store = tx.objectStore('cached-conversations');
    
    return store.put(conversation);
  }

  async getCachedConversations(limit: number = 50) {
    if (!this.db) await this.init();
    
    const tx = this.db!.transaction(['cached-conversations'], 'readonly');
    const store = tx.objectStore('cached-conversations');
    const index = store.index('timestamp');
    
    const conversations: any[] = [];
    const cursorRequest = index.openCursor(null, 'prev');
    
    return new Promise((resolve) => {
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor && conversations.length < limit) {
          conversations.push(cursor.value);
          cursor.continue();
        } else {
          resolve(conversations);
        }
      };
    });
  }
}